// Signage operator-dashboard Sentry initialisation.
//
// Reads VITE_SENTRY_DSN_FRAME from Cloudflare Pages env. When unset,
// initSentry() is a no-op — local dev and self-hosted instances pay no
// cost without explicit opt-in.
//
// Different from webplayer's Sentry setup: Session Replay IS enabled here.
// Operators interact with complex flows (multi-step pairing wizards,
// drag-and-drop playlist builders, modal dialogues) where "I clicked X
// and got error Y" is much faster to triage from a replay than from a
// stack trace alone.

import * as Sentry from '@sentry/react'

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN_FRAME as string | undefined
  if (!dsn) return

  try {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || 'production',
      release: (import.meta.env.VITE_RELEASE as string) || undefined,

      tracesSampleRate: 0.1,
      sendDefaultPii: false,

      // ── Session Replay ────────────────────────────────────────────────
      // Sample 0% of normal sessions; record 100% of sessions where an
      // error fires. The replayIntegration buffers the last ~30s, so when
      // an error happens we ship video of the steps that led to it.
      // Free-tier quota is 50 replays/month — this pattern typically yields
      // 10-30/month for an active product, well within budget.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Defaults: maskAllInputs=true (password/token fields hidden),
          // maskAllText=false (visible text is recorded — needed to see
          // which button was clicked).
          // Network traffic is recorded for endpoints listed below; bodies
          // are not captured (keeps tokens/PII out of replays).
          networkDetailAllowUrls: [],
          networkCaptureBodies: false,
        }),
      ],

      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
      ],
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[sentry] init failed', e)
  }
}
